import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MasterRequestFields, emptyProspects } from "@/components/MasterRequestFields";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Plus } from "lucide-react";

const TYPE_LABEL = { dealer: "Dealer", master: "Master", customer: "Cliente" };

export default function AgentInvites() {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company_name: "", vat_number: "", phone: "", address: "", invite_type: "dealer", master_prospects: emptyProspects() });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/agent/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.email.trim()) { toast.error("Inserisci l'email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/agent/invites", form);
      setOpen(false); load();
      try {
        const result = await api.post(`/agent/invites/${data.id}/send`);
        if (result.data.email_sent) toast.success(`Invito inviato a ${data.email}`);
        else toast.warning("Invito creato, email non inviata. Puoi copiare il link o riprovare l’invio dalla lista.");
      } catch { toast.warning("Invito creato, email non inviata. Usa il link o riprova dalla lista."); }
      setForm({ email: "", name: "", company_name: "", vat_number: "", phone: "", address: "", invite_type: "dealer", master_prospects: emptyProspects() });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nell’invio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="agent-invites-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Commerciale</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Inviti</h1>
          <p className="text-xs text-muted-foreground mt-1" data-testid="invite-private-channel-note">Gli inviti riservati a dealer e candidati Master restano disponibili anche quando le registrazioni pubbliche sono chiuse. Il destinatario verifica i dati e sceglie personalmente la password.</p>
          <p className="text-sm text-muted-foreground mt-1">Invita dealer, master e clienti a registrarsi: verranno collegati automaticamente a te.</p>
        </div>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="invite-open"><Plus className="w-4 h-4" /> Nuovo invito</Button>
      </div>
      <div className="grid gap-3">
        {invites.map((i) => (
          <Card key={i.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`invite-row-${i.id}`}>
            <div className="min-w-0"><h3 className="font-head font-semibold truncate">{i.name || i.email}</h3><p className="text-sm text-muted-foreground truncate">{i.email} · {(i.created_at || "").slice(0, 10)}</p></div>
            <div className="flex flex-wrap items-center gap-2">
              {i.link && <><Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(i.link).then(() => toast.success("Link copiato")).catch(() => toast.error("Copia non disponibile"))} data-testid={`invite-copy-${i.id}`}>Copia link riservato</Button><Button size="sm" variant="outline" disabled={busy} onClick={async () => { setBusy(true); try { const { data } = await api.post(`/agent/invites/${i.id}/send`); data.email_sent ? toast.success("Invito inviato") : toast.error("Email non inviata"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } }} data-testid={`invite-email-${i.id}`}>Invia email</Button></>}
              <Badge variant="outline" data-testid={`invite-type-${i.id}`}>{TYPE_LABEL[i.invite_type] || "Dealer"}</Badge>
              <Badge className={i.status === "registered" ? "bg-teal-600" : "bg-amber-500"}>{i.status === "registered" ? "Registrato" : "Invitato"}</Badge>
            </div>
          </Card>
        ))}
        {invites.length === 0 && <p className="text-muted-foreground py-6 text-center">Nessun invito ancora.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="agency-invite-dialog">
          <DialogHeader><DialogTitle>Nuovo invito</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo di invitato</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {["dealer", "master", "customer"].map((t) => (
                  <Button key={t} type="button" variant={form.invite_type === t ? "default" : "outline"} onClick={() => setForm({ ...form, invite_type: t })} data-testid={`invite-type-select-${t}`}>{TYPE_LABEL[t]}</Button>
                ))}
              </div>
            </div>
            <div><Label>Email</Label><Input className="mt-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
            <div><Label>Nome e cognome referente</Label><Input required={form.invite_type !== "customer"} className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
            {form.invite_type !== "customer" && <>{[["company_name", "Ragione sociale", "text"], ["vat_number", "Partita IVA", "text"], ["phone", "Telefono", "tel"], ["address", "Indirizzo", "text"]].map(([key, label, type]) => <div key={key}><Label htmlFor={`invite-${key}`}>{label}</Label><Input id={`invite-${key}`} type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`invite-${key}`} /></div>)}</>}
            {form.invite_type === "master" && <MasterRequestFields requested locked prospects={form.master_prospects} onProspects={(value) => setForm({ ...form, master_prospects: value })} prefix="invite-master" />}
            <Button className="w-full" disabled={busy} onClick={send} data-testid="invite-send">{busy ? "Invio…" : "Invia invito"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
